import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CheerCard from './CheerCard';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './CommsStats.css';

/* How ONE value or ONE goal lives — the block under it on /purpose/<kind>/<id>, for the admin who
   bought the programme and wants to see that what the company wrote down actually lands.

   ★ The detail behind one row of `PurposeStats` (the list on /values and /sdgs), and its two
   headline numbers — cheers naming it, events serving it — are the SAME numbers that row shows.
   The API counts them the same way on purpose; a page that disagrees with the list it came from
   is how an admin stops trusting both.

   ★ Admin-only, by the owner's call. The parent asks only when the item payload says
   `show_stats`, which is the endpoint's own engagement gate — an employee opening the same chip
   never fires a request it would be refused.

   ★ The sentences come after the tiles, as on the Cheers tab: a number says the value is named,
   the words say what it sounds like when it is. Each section says out loud when it is empty,
   because a zero here is the thing an admin should act on, not a rendering gap. */

const Tile = ({ n, label, sub }) => (
  <div className="cst-tile">
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
    {sub && <span className="cst-tile__sub">{sub}</span>}
  </div>
);

const PurposeItemStats = ({ kind, itemId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    apiRequest('GET', `/company/purpose/${kind}/${itemId}/stats`)
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [kind, itemId, t]);

  if (error) return <p className="bravos-cal__notice">{error}</p>;
  if (data === null) return <span className="auth__spinner" />;

  const lang = i18n.language;
  const cheers = data.cheers || {};
  const events = data.events || {};
  const contrib = data.contributions || {};
  const recent = data.recent_cheers || [];
  const past = data.past_events || [];
  const next = data.next_event;

  const eventLine = (e) => (
    <button
      key={e.id}
      type="button"
      className="cst-line cst-line--link"
      onClick={() => navigate(`/company-event/${e.id}/people`)}
    >
      <span className="cst-line__label">{e.name}</span>
      <span className="cst-line__sub">
        {[shortDate(e.date, lang), e.is_cancelled ? t('eventCancelled') : null]
          .filter(Boolean).join(' · ')}
      </span>
      <span className="cst-line__n">{t('pisNYes', { n: e.going })}</span>
    </button>
  );

  return (
    <div className="cst">
      <div className="cst-tiles">
        <Tile
          n={cheers.total || 0}
          label={t('pisCheers')}
          sub={cheers.recent
            ? t('pisCheersRecent', { n: cheers.recent, days: cheers.recent_days })
            : null}
        />
        <Tile
          n={cheers.receivers || 0}
          label={t('pisCheered')}
          sub={cheers.givers ? t('pisByColleagues', { n: cheers.givers }) : null}
        />
        <Tile
          n={events.total || 0}
          label={t('pisEvents')}
          sub={events.total
            ? t('pisEventsSplit', { past: events.past || 0, upcoming: events.upcoming || 0 })
            : null}
        />
        {/* "Answered yes", the one phrase the events stats own — and the distinct colleagues
            behind those answers under it, which is the reach rather than the volume. */}
        <Tile
          n={events.going || 0}
          label={t('statsAnsweredYes')}
          sub={events.people ? t('pisFromColleagues', { n: events.people }) : null}
        />
        <Tile
          n={contrib.total || 0}
          label={t('pisContrib')}
          sub={contrib.people ? t('pisByColleagues', { n: contrib.people }) : null}
        />
      </div>

      <p className="bravos-form__label mt-3">{t('pisRecentCheers')}</p>
      {recent.length === 0 ? (
        <p className="ce-note">{t('pisNoCheers')}</p>
      ) : (
        <div>
          {recent.map((cheer) => <CheerCard key={cheer.id} cheer={cheer} />)}
        </div>
      )}

      <p className="bravos-form__label mt-3">{t('pisEventsTitle')}</p>
      {!next && past.length === 0 ? (
        <p className="ce-note">{t('pisNoEvents')}</p>
      ) : (
        <div className="cst-breakdown">
          {next && (
            <>
              <p className="ce-note">{t('pisNextEvent')}</p>
              {eventLine(next)}
            </>
          )}
          {past.length > 0 && (
            <>
              <p className="ce-note mt-2">{t('pisPastEvents')}</p>
              {past.map(eventLine)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PurposeItemStats;
